import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-950/95 backdrop-blur-xl border-b border-background-800/60'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 cursor-pointer group">
            <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
              <i className="ri-compass-3-fill text-xl text-background-950"></i>
            </div>
            <span className="text-lg font-heading font-bold text-foreground-50 tracking-wide">
              成人导航
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                isActive('/')
                  ? 'bg-primary-500/15 text-primary-400'
                  : 'text-foreground-300 hover:text-foreground-100 hover:bg-background-800/60'
              }`}
            >
              首页
            </Link>
            {categories.slice(0, 5).map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  location.pathname.includes(cat.slug)
                    ? 'bg-primary-500/15 text-primary-400'
                    : 'text-foreground-300 hover:text-foreground-100 hover:bg-background-800/60'
                }`}
              >
                {cat.name}
              </Link>
            ))}
            <div className="relative ml-2">
              <div className="group/cat relative">
                <button className="px-4 py-2 rounded-lg text-sm font-medium text-foreground-300 hover:text-foreground-100 hover:bg-background-800/60 transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center gap-1">
                  更多分类
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </button>
                <div className="absolute top-full right-0 mt-2 w-48 bg-background-900 border border-background-700/60 rounded-xl p-2 opacity-0 invisible group-hover/cat:opacity-100 group-hover/cat:visible transition-all duration-200 shadow-2xl">
                  {categories.slice(5).map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className="block px-3 py-2 rounded-lg text-sm text-foreground-300 hover:text-foreground-100 hover:bg-background-800/60 transition-all duration-150 whitespace-nowrap cursor-pointer"
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className="w-9 h-9 rounded-lg flex items-center justify-center text-foreground-300 hover:text-foreground-100 hover:bg-background-800/60 transition-all duration-200 cursor-pointer"
              >
                <i className={`text-lg ${searchOpen ? 'ri-close-line' : 'ri-search-line'}`}></i>
              </button>
              {searchOpen && (
                <div className="absolute right-0 top-full mt-2 w-72 bg-background-900 border border-background-700/60 rounded-xl p-3 shadow-2xl">
                  <div className="flex items-center gap-2 bg-background-800 rounded-lg px-3 py-2">
                    <i className="ri-search-line text-foreground-500 text-sm"></i>
                    <input
                      type="text"
                      placeholder="搜索网站、文章..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="bg-transparent border-none outline-none text-sm text-foreground-100 placeholder-foreground-600 flex-1"
                    />
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden w-9 h-9 rounded-lg flex items-center justify-center text-foreground-300 hover:text-foreground-100 hover:bg-background-800/60 transition-all duration-200 cursor-pointer"
            >
              <i className={`text-lg ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-950/98 backdrop-blur-xl border-t border-background-800/60">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
                isActive('/')
                  ? 'bg-primary-500/15 text-primary-400'
                  : 'text-foreground-300 hover:text-foreground-100 hover:bg-background-800/60'
              }`}
            >
              首页
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
                  location.pathname.includes(cat.slug)
                    ? 'bg-primary-500/15 text-primary-400'
                    : 'text-foreground-300 hover:text-foreground-100 hover:bg-background-800/60'
                }`}
              >
                <span className="flex items-center gap-2">
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.name}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}